import { ReactNode } from 'react';
import { ContentfulLogo } from '@stadionhq-v3/contentful-ui';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import type { Asset, CometTeam } from '@graphql/index';
import { assetTransform } from '../transforms/components/atoms/asset.transform';

/**
 * Crest render size. The referee card's crest slot maxes at 56px (Small) /
 * 40px (X-Large) and uses object-fit: contain, so 40 is ample; ContentfulLogo
 * emits a responsive srcset regardless.
 */
const CREST_SIZE = 40;

/**
 * Build a `dataProviderId -> crest node` lookup from cleaned `cometTeam` entries.
 *
 * The referee card renders on a light background, so we use the standard
 * `clubCrest` (not `clubCrestInverse`). Teams with no crest are simply absent
 * from the map — resolve them via the fallback (see makeCometTeamCrestResolver).
 *
 * Spreads the cleaned `clubCrest` (a flattened Contentful asset carrying
 * url/width/height/description) into ContentfulLogo, matching the existing
 * `getClubCrest` / lineUps-transform pattern.
 */
export const buildCometTeamCrestMap = (
  teams: CleanContentful<CometTeam>[]
): Map<string, ReactNode> => {
  const map = new Map<string, ReactNode>();

  teams.forEach((team) => {
    const id = team.dataProviderId;
    const crest = team.clubCrest;

    if (!id || !crest) {
      return;
    }

    map.set(
      id,
      <ContentfulLogo
        {...crest}
        alt={team.name ?? ''}
        width={CREST_SIZE}
        height={CREST_SIZE}
      />
    );
  });

  return map;
};

/**
 * Resolve a team's crest node by `dataProviderId` (== the API teamId), falling
 * back to the Site Settings "Fallback Club Crest" when the team has no cometTeam
 * entry, or an entry without a `clubCrest`. Returns `undefined` only when no
 * real crest matches AND no fallback is configured — the card's crest slot then
 * degrades gracefully to name-only.
 *
 * @param teams             Cleaned cometTeam entries (from getCometTeams).
 * @param fallbackClubCrest siteSettings.fallbackClubCrest (passed from the server).
 */
export const makeCometTeamCrestResolver = (
  teams: CleanContentful<CometTeam>[],
  fallbackClubCrest?: CleanContentful<Asset>
) => {
  const crestMap = buildCometTeamCrestMap(teams);

  const fallbackNode: ReactNode = fallbackClubCrest ? (
    <ContentfulLogo
      {...assetTransform(fallbackClubCrest)}
      width={CREST_SIZE}
      height={CREST_SIZE}
    />
  ) : undefined;

  return (teamId: string | number | null | undefined): ReactNode => {
    if (teamId === null || teamId === undefined) {
      return fallbackNode;
    }
    return crestMap.get(String(teamId)) ?? fallbackNode;
  };
};
